import React, { useState, useEffect } from 'react'
import { Container, Row, Col, Card, CardBody, Form, FormGroup, Label, Input, Button, Alert } from 'reactstrap'
import { Breadcrumbs } from '../../AbstractElements'
import HeaderCard from '../Common/Component/HeaderCard'
import { useNavigate, useParams } from 'react-router-dom'
import { getIdData } from '../../lib/instructorinfo'
import { updateData } from '../../lib/profiles'
import { useLayout } from '../../Hooks/useLayout'
import { useRoleBasedUrl } from '../../Hooks/useRoleBasedUrl'

const AdminInstructorEdit = () => {
	const [formData, setFormData] = useState({
		name: '',
		surname: '',
		phone: '',
		username: ''
	})
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState('')

	const navigate = useNavigate()
	const { id, type } = useParams()
	const layout = useLayout()
	const role = useRoleBasedUrl()

	const fetchInstructorData = async () => {
		try {
			setLoading(true)
			const info = await getIdData(id)
			if (info && info[0]) {
				setFormData({
					name: info[0].name || '',
					surname: info[0].surname || '',
					phone: info[0].phone || '',
					username: info[0].username || ''
				})
			}
		} catch (error) {
			setError('Failed to load instructor information: ' + error.message)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		if (type === 'Update') {
			fetchInstructorData()
		}
	}, [id, type])

	const handleInputChange = (e) => {
		const { name, value } = e.target
		setFormData((prev) => ({
			...prev,
			[name]: value
		}))
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setSaving(true)
		setError('')

		try {
			await updateData({
				id: id,
				username: formData.username,
				name: formData.name,
				surname: formData.surname,
				phone: formData.phone
			})

			if (type === 'Add') {
				navigate(`${process.env.PUBLIC_URL}/${role}/instructors/success/${id}/${layout}`)
			} else {
				navigate(`${process.env.PUBLIC_URL}/${role}/instructors/detail/${id}/${layout}`)
			}
		} catch (error) {
			setError('Failed to save instructor information: ' + error.message)
		} finally {
			setSaving(false)
		}
	}

	if (loading) {
		return (
			<>
				<Breadcrumbs parent="Instructor Management" title="Loading..." mainTitle="Loading..." />
				<Container fluid={true}>
					<Row>
						<Col sm="12">
							<Card>
								<CardBody>
									<div className="text-center">
										<div className="spinner-border" role="status">
											<span className="sr-only">Loading...</span>
										</div>
									</div>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</Container>
			</>
		)
	}

	return (
		<>
			<Breadcrumbs
				parent="Instructor Management"
				title={type === 'Add' ? 'Add Instructor' : 'Update Instructor'}
				mainTitle={type === 'Add' ? 'Add Instructor' : 'Update Instructor'}
			/>
			<Container fluid={true}>
				<Row>
					<Col sm="12">
						<Card>
							<HeaderCard
								title={type === 'Add' ? 'Add Instructor' : 'Update Instructor'}
								subTitle={type === 'Add' ? 'Complete instructor information' : 'Update instructor details'}
							/>
							<CardBody>
								<Form onSubmit={handleSubmit}>
									{error && (
										<Alert color="danger" className="mb-3">
											{error}
										</Alert>
									)}
									<Row>
										<Col md="6">
											<FormGroup>
												<Label for="name">Name</Label>
												<Input
													type="text"
													id="name"
													name="name"
													value={formData.name}
													onChange={handleInputChange}
													placeholder="Enter Name"
													required
												/>
											</FormGroup>
										</Col>
										<Col md="6">
											<FormGroup>
												<Label for="surname">Surname</Label>
												<Input
													type="text"
													id="surname"
													name="surname"
													value={formData.surname}
													onChange={handleInputChange}
													placeholder="Enter Surname"
													required
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="6">
											<FormGroup>
												<Label for="phone">Phone</Label>
												<Input
													type="tel"
													id="phone"
													name="phone"
													value={formData.phone}
													onChange={handleInputChange}
													placeholder="123-45-67"
													pattern="[0-9]{3}-[0-9]{2}-[0-9]{2}"
												/>
											</FormGroup>
										</Col>
										<Col md="6">
											<FormGroup>
												<Label for="username">Username</Label>
												<Input
													type="text"
													id="username"
													name="username"
													value={formData.username}
													onChange={handleInputChange}
													placeholder="Enter Username"
												/>
											</FormGroup>
										</Col>
									</Row>

									<div className="d-flex justify-content-between mt-4">
										<Button type="submit" color="primary" disabled={saving}>
											{saving ? 'Saving...' : type === 'Add' ? 'Create Instructor' : 'Update Instructor'}
										</Button>
										<Button
											type="button"
											color="secondary"
											onClick={() => {
												if (type === 'Add') {
													navigate(`${process.env.PUBLIC_URL}/${role}/instructors/${layout}`)
												} else {
													navigate(`${process.env.PUBLIC_URL}/${role}/instructors/detail/${id}/${layout}`)
												}
											}}
											disabled={saving}
										>
											Cancel
										</Button>
									</div>
								</Form>
							</CardBody>
						</Card>
					</Col>
				</Row>
			</Container>
		</>
	)
}

export { AdminInstructorEdit }
